export interface ModelOption {
  provider: string;
  providerLabel: string;
  model: string;
  label: string;
  /** Optional one-word tier hint rendered as a badge in the picker (e.g. "fastest"). */
  hint?: string;
}

// NOTE: DEFAULT_CHAT_MODEL below is positional — Auto must stay first (SGL-368, 2026-09-28:
// Auto is the first entry so it's the first thing a customer sees, and DEFAULT_CHAT_MODEL exists
// for display purposes only — e.g. what a picker shows before anything is contextually chosen. It
// is deliberately NOT used anywhere a concrete stored/created-agent default is decided; those call
// sites use SONNET_5_MODEL below instead, because "New Agents keep Sonnet 5 stored" and "no
// existing customer sees a change they did not ask for" are non-negotiable (Auto is opt-in).
// Sonnet 5 is the default for every Agent type except the AI Visibility Manager, which defaults to
// Opus 5 (see DEFAULT_MODEL_BY_AGENT_TYPE, decided 2026-09-16 SGL-315). Both reason before
// answering and cost more per reply than 4.6; that cost is accepted. Existing Agents keep the
// model stored on them, unless it is retired (RETIRED_MODEL_IDS, SGL-423).
export const CHAT_MODEL_OPTIONS: ModelOption[] = [
  // Auto (SGL-368) — resolved per turn on the backend (chat-service/app/services/auto_model.py).
  // Never persisted by agent creation defaults; only reached by explicit user selection.
  // No "recommended" hint here: the recommendation is per Agent type (defaultModelForAgentType),
  // so the picker tags it at render time — Sonnet 5, or Opus 5 for the Visibility Manager (SGL-423).
  { provider: 'bedrock_claude', providerLabel: 'Claude', model: 'auto',                      label: 'Auto' },
  // Claude (Bedrock) — Sonnet 5 is the default.
  { provider: 'bedrock_claude', providerLabel: 'Claude', model: 'claude-sonnet-5',           label: 'Sonnet 5' },
  { provider: 'bedrock_claude', providerLabel: 'Claude', model: 'claude-opus-5',             label: 'Opus 5' },
  { provider: 'bedrock_claude', providerLabel: 'Claude', model: 'claude-fable-5-1',          label: 'Fable 5.1',    hint: 'premium' },
  { provider: 'bedrock_claude', providerLabel: 'Claude', model: 'claude-opus-4-8',           label: 'Opus 4.8' },
  { provider: 'bedrock_claude', providerLabel: 'Claude', model: 'claude-opus-4-7',           label: 'Opus 4.7' },
  { provider: 'bedrock_claude', providerLabel: 'Claude', model: 'claude-haiku-4-5-20251001', label: 'Haiku 4.5',    hint: 'fastest' },
  // OpenAI — GPT-5.2 removed (legacy, superseded by 5.6); GPT-5.6 ships as three tiers.
  { provider: 'openai',         providerLabel: 'GPT',    model: 'gpt-6-astra',               label: 'GPT-6 Astra',   hint: 'premium' },
  { provider: 'openai',         providerLabel: 'GPT',    model: 'gpt-5.1',                   label: 'GPT-5.1' },
  { provider: 'openai',         providerLabel: 'GPT',    model: 'gpt-5.6-luna',              label: 'GPT-5.6 Luna',  hint: 'fastest' },
  { provider: 'openai',         providerLabel: 'GPT',    model: 'gpt-5.6-terra',             label: 'GPT-5.6 Terra', hint: 'balanced' },
  { provider: 'openai',         providerLabel: 'GPT',    model: 'gpt-5.6-sol',               label: 'GPT-5.6 Sol',   hint: 'premium' },
  // Perplexity — chat-picker only; agents cannot be created on this provider
  { provider: 'perplexity',     providerLabel: 'Perplexity', model: 'sonar-pro',             label: 'Sonar Pro' },
  { provider: 'perplexity',     providerLabel: 'Perplexity', model: 'sonar-deep-research',   label: 'Deep Research' },
];

export const DEFAULT_CHAT_MODEL = CHAT_MODEL_OPTIONS[0];

/**
 * Models that are no longer offered and never where a chat starts (SGL-423, 2026-10-09).
 * An Agent stores whichever model was the default the day it was created, so most of them
 * still carry one of these. Chat-service applies the same list server-side (RETIRED_MODELS
 * in chat-service/app/services/auto_model.py) and swaps in a current model of the SAME
 * provider (replacementModelFor), so the owner is never asked to do anything. Keep both lists
 * in step.
 */
export const RETIRED_MODEL_IDS: ReadonlySet<string> = new Set([
  'claude-sonnet-4-6',
  'gpt-5.2',
  'gpt-4o',
  'gpt-4o-mini',
  'gpt-4.1',
]);

export const isRetiredModel = (model: string | null | undefined): boolean =>
  !!model && RETIRED_MODEL_IDS.has(model);

/**
 * The concrete "sensible default" model — Sonnet 5. Use this (never DEFAULT_CHAT_MODEL, which is
 * Auto as of SGL-368) anywhere a NEW agent's stored model, or a session's starting/reset model, is
 * decided: agent creation, a fresh composer session, and resetting off a chip-pinned model. Auto is
 * opt-in — a customer reaches it only by selecting it deliberately.
 */
export const SONNET_5_MODEL = CHAT_MODEL_OPTIONS.find((o) => o.model === 'claude-sonnet-5')!;

/**
 * What an OpenAI Agent is recommended to run on, and what a retired GPT model becomes. Mirrors
 * OPENAI_REPLACEMENT_MODEL in chat-service/app/services/auto_model.py. An Agent that is on OpenAI
 * stays on OpenAI: its knowledge files live in an OpenAI vector store that only the OpenAI
 * adapter reads, so moving it to Claude would silently drop them (SGL-423 follow-up).
 */
export const OPENAI_RECOMMENDED_MODEL = CHAT_MODEL_OPTIONS.find((o) => o.model === 'gpt-5.6-terra')!;

/** True for ids that belong to OpenAI — the same prefixes chat-service uses to pick the provider. */
export const isOpenAiModelId = (model: string | null | undefined): boolean =>
  !!model && /^(gpt-|o1-|o3-)/.test(model);

/**
 * Default model per Agent type, applied when an Agent is created (SGL-315, 2026-09-16).
 * The Visibility Manager reads a site and decides what to publish, low volume and high stakes,
 * so it gets Opus 5. Everything else (Social Media Manager, Receptionist owner chat, Sagelyn
 * Assistant, general) gets Sonnet 5. The customer-facing Receptionist SMS path is a separate
 * backend route and is not governed by this map. Existing Agents keep their stored model unless
 * it is retired (RETIRED_MODEL_IDS), in which case a chat starts on this default.
 */
export const DEFAULT_MODEL_BY_AGENT_TYPE: Record<string, string> = {
  visibility_manager: 'claude-opus-5',
};

/** The ModelOption an Agent of this type starts on; SONNET_5_MODEL when the type has no override. */
export const defaultModelForAgentType = (agentType: string | null | undefined): ModelOption => {
  const id = agentType ? DEFAULT_MODEL_BY_AGENT_TYPE[agentType] : undefined;
  return (id && CHAT_MODEL_OPTIONS.find((o) => o.model === id)) || SONNET_5_MODEL;
};

/**
 * The model recommended for an Agent: GPT-5.6 Terra when the Agent is on OpenAI, otherwise the
 * default for its type (Sonnet 5, or Opus 5 for the Visibility Manager). `provider` is the
 * Agent's provider id ('openai' / 'bedrock_claude') when known.
 */
export const recommendedModelFor = (
  agentType: string | null | undefined,
  provider: string | null | undefined,
): ModelOption => (provider === 'openai' ? OPENAI_RECOMMENDED_MODEL : defaultModelForAgentType(agentType));

/**
 * The model that stands in for an Agent's stored one when that cannot be used as is (retired, or
 * missing from the list): a current model of the SAME provider. A GPT model becomes GPT-5.6 Terra
 * so the Agent keeps its OpenAI vector store; anything else becomes the default for its type.
 * Mirrors swap_retired_model in chat-service/app/services/auto_model.py.
 */
export const replacementModelFor = (
  storedModel: string | null | undefined,
  agentType: string | null | undefined,
): ModelOption =>
  isOpenAiModelId(storedModel) ? OPENAI_RECOMMENDED_MODEL : defaultModelForAgentType(agentType);

/**
 * The ModelOption a chat session with this Agent should start on (SGL-368 follow-up,
 * 2026-09-30). Honors whatever is actually stored on the Agent record — including `auto` —
 * and only falls back to a replacement (replacementModelFor: same provider) when the Agent has
 * no stored model, it is retired (SGL-423: RETIRED_MODEL_IDS), or it names something no longer in
 * CHAT_MODEL_OPTIONS. This is what makes an Agent's own Model field (set via Edit Agent)
 * actually reach a real conversation, instead of every session silently starting on
 * SONNET_5_MODEL regardless of what the Agent stores.
 */
export const resolveAgentStartingModel = (
  storedModel: string | null | undefined,
  agentType: string | null | undefined,
): ModelOption => {
  const stored =
    storedModel && !isRetiredModel(storedModel)
      ? CHAT_MODEL_OPTIONS.find((o) => o.model === storedModel)
      : undefined;
  return stored ?? replacementModelFor(storedModel, agentType);
};

/**
 * The model the Edit Agent form selects when it opens (SGL-423). A retired stored model opens as
 * its replacement (replacementModelFor), so pressing Save migrates the Agent without the owner
 * having to find the right option. Exception: an Agent's provider decides where its knowledge
 * files and ids live, so the form never switches provider (SGL-257); when the replacement is on
 * another provider the stored value is left as is and the picker shows it as retired.
 */
export const editableModelFor = (
  storedModel: string | null | undefined,
  agentType: string | null | undefined,
  agentProvider: string | null | undefined,
): string => {
  if (!storedModel) return '';
  if (!isRetiredModel(storedModel)) return storedModel;
  const replacement = replacementModelFor(storedModel, agentType);
  return !agentProvider || agentProvider === replacement.provider ? replacement.model : storedModel;
};

/**
 * Model a chip switches the picker to when the user selects it, keyed by chip/tool id.
 *
 * Forward direction only: selecting the chip sets the model. The user can still change the
 * model afterwards — the picker always shows what will actually be sent.
 */
export const CHIP_PREFERRED_MODEL: Record<string, string> = {
  perplexity_search: 'sonar-pro',
  deep_research: 'sonar-deep-research',
  // The blog chip used to pin Sonnet 5 over the 4.6 default; Sonnet 5 is the default now.
};

/**
 * Reverse direction, and deliberately NOT the inverse of the map above: only models used
 * *exclusively* by one chip belong here, because picking such a model implies the chip.
 *
 * The Perplexity models qualify — nothing else uses them. Sonnet 5 does not: it is a general
 * model in the picker, so selecting it must not silently drop the user into Blog mode.
 */
export const MODEL_EXCLUSIVE_CHIP: Record<string, string> = {
  'sonar-pro': 'perplexity_search',
  'sonar-deep-research': 'deep_research',
};

/** The ModelOption a chip prefers, or undefined when the chip doesn't pin a model. */
export const modelForChip = (chipId: string | null | undefined): ModelOption | undefined => {
  const target = chipId ? CHIP_PREFERRED_MODEL[chipId] : undefined;
  return target ? CHAT_MODEL_OPTIONS.find((o) => o.model === target) : undefined;
};

/**
 * Models that cost materially more per reply than the Sonnet 5 default (Sonnet 5 also reasons,
 * but it is the default, so a notice under every composer would say nothing). Drives the cost/latency notice shown under the composer when one
 * of these is selected (see ChatInputArea). Single source of truth — add a model here, not
 * at the call site, if it gets the same always-on-reasoning behavior in the future.
 */
export const HIGH_TOKEN_USAGE_MODELS = new Set(['claude-opus-5', 'claude-fable-5-1', 'gpt-6-astra']);

export const isHighTokenUsageModel = (model: string | undefined): boolean =>
  !!model && HIGH_TOKEN_USAGE_MODELS.has(model);

/**
 * Models whose provider requires human safety review as a condition of access. On Amazon
 * Bedrock, Claude Fable 5.x prompts and outputs are retained by AWS for up to 30 days and may
 * be reviewed by AWS personnel (not shared with Anthropic, not used for training). No other
 * model in the lineup does this. The notice must appear wherever the model is chosen, and the
 * Terms (§8) and Privacy Policy (§5) name the same models, so keep the three in step.
 */
export const SAFETY_REVIEW_MODELS = new Set(['claude-fable-5-1']);

export const SAFETY_REVIEW_NOTICE =
  'Conversations with this model are kept by Amazon Web Services for up to 30 days and may be reviewed by AWS for safety. Not shared with Anthropic, not used for training.';

export const isSafetyReviewModel = (model: string | undefined): boolean =>
  !!model && SAFETY_REVIEW_MODELS.has(model);
